import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, FileText, Eye, Download } from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { getContent } from "../i18n/data";
import { useSeo } from "../lib/seo";

const ease = [0.16, 1, 0.3, 1];

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function Landing() {
  const { t, lang } = useI18n();
  const { manifesto, methodology, measures, dna, brands } = getContent(lang);
  const brandCards = [brands.gout, brands.vetta, brands.aura];
  const brandLogos = {
    gout: "/brand/gout-black.png",
    vetta: "/brand/vetta-black.png",
    aura: "/brand/aura-black.png",
  };

  const dossiers = [
    { key: "omnia", title: "OMNIA · Neo-Designer", sub: "The Design Intelligence Group", file: "/docs/omnia-neo-designer.pdf" },
    { key: "gout", title: "GOÛT · Design Intelligence Studio", sub: "Studio", file: "/docs/gout-studio.pdf" },
    { key: "gout", title: "GOÛT READY", sub: "Ready-to-specify", file: "/docs/gout-ready.pdf" },
    { key: "vetta", title: "VETTA · From Design to Market", sub: "Manufacturing", file: "/docs/vetta-from-design-to-market.pdf" },
    { key: "aura", title: "AURA · Living Intelligence", sub: "Commerce", file: "/docs/aura-living-intelligence.pdf" },
  ];

  const [lead, setLead] = useState({ name: "", email: "", company: "", interest: "OMNIA", message: "" });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useSeo({
    title: "OMNIA — Everything Connected · The Design Intelligence Group",
    description: t("hero.body"),
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "OMNIA Intelligence Group",
      slogan: "Everything Connected",
      description: t("hero.body"),
      brand: ["OMNIA", "GOÛT", "VETTA", "AURA"],
    },
  });

  const submitLead = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/leads", lead);
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="bg-cream text-navy overflow-x-hidden">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-28 pb-20 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
          {/* Left — content */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease }} className="overline text-navy/40 mb-8"
            >
              {t("hero.overline")}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease, delay: 0.1 }}
              className="font-editorial font-light text-6xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight"
            >
              {t("hero.l1")}<br />
              <span className="text-gradient-violet italic">{t("hero.l2")}</span>
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.3 }}
              className="mt-10 space-y-8"
            >
              <p className="text-lg sm:text-xl text-navy/60 max-w-xl leading-relaxed">{t("hero.body")}</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/login" data-testid="hero-cta-os"
                  className="group inline-flex items-center gap-2 rounded-full bg-navy text-white px-6 py-3.5 hover:bg-navy-soft transition-colors">
                  {t("hero.ctaOs")}
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
                <a href="#ecosistema" data-testid="hero-cta-eco"
                  className="inline-flex items-center gap-2 rounded-full border border-navy/20 px-6 py-3.5 hover:bg-navy hover:text-white transition-colors">
                  {t("hero.ctaEco")}
                </a>
                <Link to="/shop" data-testid="hero-cta-shop"
                  className="inline-flex items-center gap-2 rounded-full border border-navy/20 px-6 py-3.5 hover:bg-navy hover:text-white transition-colors">
                  {t("shop.cart") === "Carrito" ? "Tienda AURA" : "AURA Shop"}
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Right — editorial image */}
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease, delay: 0.35 }}
            className="relative"
          >
            <div className="relative aspect-[4/5] lg:aspect-[3/4] rounded-[2rem] overflow-hidden shadow-2xl shadow-navy/10">
              <img src="/brand/hero-interior.jpg" alt="OMNIA · Design Intelligence" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/25 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-5 left-5 right-5 lg:left-8 lg:right-8 bg-cream/90 backdrop-blur-md border border-navy/10 rounded-2xl px-5 py-4 flex items-center justify-between">
              <div>
                <div className="overline text-navy/40 text-[0.6rem]">OMNIA · GOÛT · VETTA · AURA</div>
                <div className="font-editorial text-lg tracking-tight mt-0.5">{t("hero.l1")} {t("hero.l2")}</div>
              </div>
              <ArrowUpRight className="w-5 h-5 text-violet shrink-0" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* MANIFESTO STRIP */}
      <section className="bg-navy text-cream grain py-24 md:py-32">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="overline text-cream/40 mb-16">{t("manifestoLabel")}</div>
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-14">
            {manifesto.map((m, i) => (
              <Reveal key={m.si} delay={i * 0.08}>
                <div className="border-t border-cream/15 pt-6">
                  <p className="text-cream/40 text-lg mb-2">{m.no}</p>
                  <p className="font-editorial text-4xl md:text-5xl leading-tight">{m.si}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NEO-DESIGNER */}
      <section id="neo" className="py-24 md:py-36 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1fr_1.1fr] gap-16 items-center">
          <Reveal>
            <div className="overline text-violet mb-6">{t("neo.overline")}</div>
            <h2 className="font-editorial text-5xl md:text-6xl leading-[1.02] tracking-tight whitespace-pre-line">{t("neo.h")}</h2>
            <p className="mt-8 text-navy/60 leading-relaxed max-w-lg">{t("neo.body")}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-3">
              {dna.map((d) => (
                <span key={d}
                  className="text-sm px-5 py-2.5 rounded-full border border-navy/15 bg-white/60 hover:border-violet hover:text-violet transition-colors">
                  {d}
                </span>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="max-w-7xl mx-auto mt-16 md:mt-24">
            <div className="relative overflow-hidden rounded-2xl aspect-[21/9] bg-cream">
              <img src="https://images.unsplash.com/photo-1760611655987-d348d6d28174?auto=format&fit=crop&w=2400&q=80"
                alt="OMNIA — sistemas de diseño"
                className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10">
                <div className="overline text-cream/70 mb-1">OMNIA · Everything Connected</div>
                <p className="font-editorial text-cream text-2xl md:text-4xl tracking-tight">{t("neo.h").replace("\n", " ")}</p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ECOSYSTEM */}
      <section id="ecosistema" className="py-20 md:py-28 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <div>
                <div className="overline text-navy/40 mb-4">{t("eco.overline")}</div>
                <h2 className="font-editorial text-5xl md:text-6xl tracking-tight whitespace-pre-line">{t("eco.h")}</h2>
              </div>
              <p className="text-navy/60 max-w-sm">{t("eco.body")}</p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {brandCards.map((b, i) => (
              <Reveal key={b.key} delay={i * 0.1}>
                <Link to={`/marca/${b.key}`} data-testid={`eco-card-${b.key}`}
                  className="group block h-full bg-white border border-navy/10 hover:-translate-y-1 hover:border-navy/25 transition-all duration-500 overflow-hidden">
                  <div className="aspect-[4/3] overflow-hidden bg-cream">
                    <img src={b.image} alt={b.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-8">
                    <div className="flex items-center justify-between">
                      <img src={brandLogos[b.key]} alt={b.name}
                        className="h-8 w-auto object-contain max-w-[170px]" />
                      <ArrowUpRight className="w-5 h-5 text-navy/30 group-hover:text-violet group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                    </div>
                    <div className="overline text-navy/40 mt-2">{b.tagline}</div>
                    <p className="mt-4 text-sm text-navy/60 leading-relaxed">{b.description}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* METHODOLOGY */}
      <section id="metodologia" className="py-24 md:py-32 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="overline text-navy/40 mb-4">{t("method.overline")}</div>
            <h2 className="font-editorial text-5xl md:text-6xl tracking-tight mb-16">{t("method.h")}</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-navy/10 border border-navy/10">
            {methodology.map((m, i) => (
              <Reveal key={m.n} delay={(i % 3) * 0.08}>
                <div className="bg-cream p-8 md:p-10 h-full hover:bg-white transition-colors duration-500">
                  <div className="font-mono text-sm text-violet mb-6">{m.n}</div>
                  <h3 className="text-2xl font-medium tracking-tight mb-3">{m.title}</h3>
                  <p className="text-sm text-navy/60 leading-relaxed">{m.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DATA DRIVEN / INTELLIGENCE */}
      <section id="intelligence" className="bg-navy text-cream grain py-24 md:py-36 px-6 lg:px-10">
        <div className="relative max-w-7xl mx-auto">
          <Reveal>
            <div className="overline text-cream/40 mb-6">{t("intel.overline")}</div>
            <h2 className="font-editorial text-5xl md:text-7xl tracking-tight leading-[0.98] max-w-3xl">{t("intel.h")}</h2>
            <p className="mt-8 text-cream/60 max-w-xl leading-relaxed">{t("intel.body")}</p>
          </Reveal>

          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-cream/10 border border-cream/10">
            {measures.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.06}>
                <div className="bg-navy p-7 h-full">
                  <div className="font-mono text-xs text-cream/40 mb-8">{m.n}</div>
                  <h3 className="text-xl font-medium mb-2">{m.title}</h3>
                  <div className="overline mb-4" style={{ color: "#a78bfa" }}>{m.tag}</div>
                  <p className="text-sm text-cream/50 leading-relaxed">{m.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-14 grid sm:grid-cols-3 gap-8">
            {t("intel.stats").map((s, i) => (
              <Reveal key={s.l} delay={i * 0.1}>
                <div className="border-t border-cream/15 pt-6">
                  <div className="font-editorial text-6xl md:text-7xl text-gradient-violet">{s.v}</div>
                  <div className="overline text-cream/40 mt-3">{s.l}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRESENTATIONS / DOSSIERS */}
      <section id="dossiers" className="py-24 md:py-32 px-6 lg:px-10 border-t border-navy/10">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="overline text-navy/40 mb-4">{t("dossiers.overline")}</div>
            <h2 className="font-editorial text-5xl md:text-6xl tracking-tight max-w-2xl">{t("dossiers.h")}</h2>
            <p className="mt-4 text-navy/55 max-w-xl leading-relaxed">{t("dossiers.body")}</p>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {dossiers.map((d, i) => (
              <Reveal key={d.title} delay={(i % 3) * 0.08}>
                <div data-testid={`dossier-${i}`} className="group h-full bg-white border border-navy/10 rounded-xl p-7 flex flex-col hover:-translate-y-1 hover:border-navy/25 transition-all duration-500">
                  <div className="flex items-start justify-between">
                    {brandLogos[d.key]
                      ? <img src={brandLogos[d.key]} alt={d.title} className="h-6 w-auto object-contain max-w-[130px]" />
                      : <span className="font-sans text-lg tracking-[0.2em] text-navy">OMNIA</span>}
                    <FileText className="w-5 h-5 text-navy/25 group-hover:text-violet transition-colors" />
                  </div>
                  <h3 className="mt-6 text-xl font-medium tracking-tight leading-snug">{d.title}</h3>
                  <div className="overline text-navy/40 mt-1">{d.sub}</div>
                  <div className="mt-auto pt-7 flex gap-2">
                    <a href={d.file} target="_blank" rel="noreferrer" data-testid={`dossier-view-${i}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-navy text-white px-4 py-2 text-sm hover:bg-navy-soft transition-colors">
                      <Eye className="w-4 h-4" /> {t("dossiers.view")}
                    </a>
                    <a href={d.file} download data-testid={`dossier-download-${i}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-navy/20 px-4 py-2 text-sm hover:border-violet hover:text-violet transition-colors">
                      <Download className="w-4 h-4" /> {t("dossiers.download")}
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA / LEAD */}
      <section id="contacto" className="py-24 md:py-36 px-6 lg:px-10">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-16 items-start">
          <Reveal>
            <div className="overline text-navy/40 mb-6">{t("contact.overline")}</div>
            <h2 className="font-editorial text-5xl md:text-6xl tracking-tight leading-[1]">
              {t("contact.h1")}<br />
              <span className="text-gradient-violet italic">{t("contact.h2")}</span>
            </h2>
            <p className="mt-8 text-navy/60 leading-relaxed max-w-md">{t("contact.body")}</p>
          </Reveal>

          <Reveal delay={0.1}>
            {sent ? (
              <div data-testid="lead-success" className="bg-white border border-navy/10 rounded-lg p-10 text-center">
                <div className="w-12 h-12 rounded-full bg-gradient-violet mx-auto flex items-center justify-center">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <h3 className="mt-6 text-2xl font-medium">{t("contact.successTitle")}</h3>
                <p className="mt-2 text-navy/60">{t("contact.successBody")}</p>
              </div>
            ) : (
              <form onSubmit={submitLead} data-testid="lead-form" className="bg-white border border-navy/10 rounded-lg p-8 space-y-4">
                <input required data-testid="lead-name" placeholder={t("contact.name")} value={lead.name}
                  onChange={(e) => setLead({ ...lead, name: e.target.value })}
                  className="w-full border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
                <input required type="email" data-testid="lead-email" placeholder={t("contact.email")} value={lead.email}
                  onChange={(e) => setLead({ ...lead, email: e.target.value })}
                  className="w-full border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
                <input data-testid="lead-company" placeholder={t("contact.company")} value={lead.company}
                  onChange={(e) => setLead({ ...lead, company: e.target.value })}
                  className="w-full border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors" />
                <select data-testid="lead-interest" value={lead.interest}
                  onChange={(e) => setLead({ ...lead, interest: e.target.value })}
                  className="w-full border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors">
                  <option>OMNIA</option><option>GOÛT</option><option>VETTA</option><option>AURA</option>
                </select>
                <textarea data-testid="lead-message" placeholder={t("contact.message")} rows={3} value={lead.message}
                  onChange={(e) => setLead({ ...lead, message: e.target.value })}
                  className="w-full border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors resize-none" />
                {error && <p className="text-sm text-destructive">{error}</p>}
                <button data-testid="lead-submit"
                  className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity">
                  {t("contact.submit")}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
